import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Download, Upload, Package, RefreshCw, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export default function MigrationPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("overwrite");
  const [lastImportResult, setLastImportResult] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/migration/status")
      .then((r) => setStatus(r.data))
      .catch((e) => toast.error(errText(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const runExport = async () => {
    setExporting(true);
    const wait = toast.loading("Starting migration bundle…");
    try {
      // Kick off the background job instead of a blocking request. A synchronous
      // export of large uploads (>100s) trips Cloudflare's origin timeout (520),
      // so we start the async prep-bundle job and poll its status instead.
      await api.post("/admin/migration/prep-bundle");
      const poll = async () => {
        try {
          const { data } = await api.get("/admin/migration/prep-bundle/status");
          const job = data.job;
          if (!job) { toast.dismiss(wait); setExporting(false); return; }
          if (job.status === "queued" || job.status === "running") {
            const msg = job.phase === "backfill"
              ? `Backing up media to storage… ${job.backfill_uploaded || 0}/${job.backfill_total || 0}`
              : "Compressing bundle… large uploads can take a few minutes.";
            toast.loading(msg, { id: wait });
            setTimeout(poll, 3000);
            return;
          }
          if (job.status === "done") {
            toast.dismiss(wait);
            toast.success(`Bundle ready: ${job.bundle_filename} (${fmtBytes(job.bundle_size_bytes)})`);
            setExporting(false);
            load();
            return;
          }
          // failed
          toast.dismiss(wait);
          toast.error(`Bundle failed: ${job.error || "unknown error"}`);
          setExporting(false);
        } catch (e) {
          // Transient poll hiccup — keep trying; the job runs server-side regardless.
          setTimeout(poll, 4000);
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.dismiss(wait);
      toast.error(errText(e));
      setExporting(false);
    }
  };

  const downloadBundle = (filename) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    // Open in a new tab so the browser handles the large download
    window.open(`${base}/api/admin/migration/download/${filename}`, "_blank");
  };

  const deleteBundle = async (filename) => {
    if (!window.confirm(`Delete bundle ${filename}?`)) return;
    try {
      await api.delete(`/admin/migration/bundles/${filename}`);
      toast.success("Bundle deleted");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a bundle .zip first"); return; }
    setImporting(true);
    const wait = toast.loading("Uploading bundle… do not close the tab.");
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mode", importMode);
      // Upload streams to the server, which then restores in the BACKGROUND
      // and returns a job immediately — so the request returns well within the
      // ingress timeout even for multi-GB bundles. We then poll for progress.
      await api.post("/admin/migration/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      const poll = async () => {
        try {
          const { data } = await api.get("/admin/migration/import/status");
          const job = data.job;
          if (!job) { toast.dismiss(wait); setImporting(false); return; }
          if (job.status === "queued" || job.status === "running") {
            const msg = job.phase === "uploads"
              ? `Restoring media… ${job.uploads_written || 0}/${job.uploads_total || 0} files`
              : `Restoring database… ${job.collections_done || 0}/${job.collections_total || 0} collections`;
            toast.loading(msg, { id: wait });
            setTimeout(poll, 2500);
            return;
          }
          if (job.status === "done") {
            toast.dismiss(wait);
            setLastImportResult(job.result);
            toast.success("Bundle imported. Reloading status…");
            setImporting(false);
            load();
            return;
          }
          // failed
          toast.dismiss(wait);
          toast.error(`Import failed: ${job.error || "unknown error"}`);
          setImporting(false);
        } catch (e) {
          setTimeout(poll, 4000);
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.dismiss(wait);
      toast.error(errText(e));
      setImporting(false);
    }
  };

  const stats = status?.stats;
  const bundles = status?.bundles || [];

  return (
    <div className="space-y-6" data-testid="migration-panel">
      <Card data-testid="migration-status-card">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2"><Package size={18} /> Preview → Production migration</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              When you deploy to production, MongoDB and the uploads folder start empty. Create a bundle here, download it, then upload it in your production Admin → Migration tab. Sessions and transient job history are intentionally skipped.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="migration-refresh-btn">
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {stats && (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Documents to migrate</div>
                <div className="font-heading text-2xl font-semibold" data-testid="migration-total-docs">{stats.total_docs}</div>
                <div className="text-xs text-muted-foreground mt-1">across {Object.keys(stats.collections).length} collections</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Media files</div>
                <div className="font-heading text-2xl font-semibold" data-testid="migration-uploads-files">{stats.uploads_files}</div>
                <div className="text-xs text-muted-foreground mt-1">{fmtBytes(stats.uploads_bytes)} total</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Skipped (intentional)</div>
                <div className="text-sm mt-1">{stats.skipped_collections.join(", ") || "—"}</div>
              </div>
            </div>
          )}
          {stats && (
            <details className="rounded-lg border p-3">
              <summary className="cursor-pointer text-sm font-medium">Per-collection breakdown</summary>
              <div className="mt-3 grid gap-1 sm:grid-cols-2 text-xs">
                {Object.entries(stats.collections).map(([name, cnt]) => (
                  <div key={name} className="flex justify-between border-b border-dashed py-1">
                    <span className="text-muted-foreground">{name}</span>
                    <span className="font-mono">{cnt}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
            <Button onClick={runExport} disabled={exporting} data-testid="migration-export-btn">
              <Package size={14} className="mr-2" />
              {exporting ? "Building bundle…" : "Create migration bundle"}
            </Button>
            <p className="text-xs text-muted-foreground">Run this in <b>preview</b> right before deploying.</p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="migration-bundles-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2"><Download size={16} /> Generated bundles</CardTitle>
          <p className="text-xs text-muted-foreground">Download the latest one, then upload it in production.</p>
        </CardHeader>
        <CardContent>
          {bundles.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bundles yet — click <b>Create migration bundle</b> above.</p>
          ) : (
            <div className="space-y-2">
              {bundles.map((b) => (
                <div key={b.filename} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3" data-testid={`migration-bundle-row-${b.filename}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{b.filename}</div>
                    <div className="text-xs text-muted-foreground">{fmtBytes(b.size_bytes)} · created {new Date(b.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => downloadBundle(b.filename)} data-testid={`migration-download-${b.filename}`}>
                      <Download size={14} className="mr-2" /> Download
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteBundle(b.filename)} data-testid={`migration-delete-${b.filename}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="migration-import-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2"><Upload size={16} /> Import bundle (run this in production)</CardTitle>
          <p className="text-xs text-muted-foreground">Upload the .zip you downloaded from preview. <b>Overwrite</b> replaces every collection (recommended for a fresh production DB). <b>Merge</b> keeps existing rows and adds new ones by <code>id</code>.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="text-sm"
              data-testid="migration-import-file"
            />
            <div className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="radio" checked={importMode === "overwrite"} onChange={() => setImportMode("overwrite")} data-testid="migration-mode-overwrite" />
                <span>Overwrite</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="radio" checked={importMode === "merge"} onChange={() => setImportMode("merge")} data-testid="migration-mode-merge" />
                <span>Merge</span>
              </label>
            </div>
            <Button onClick={runImport} disabled={importing || !importFile} data-testid="migration-import-btn">
              <Upload size={14} className="mr-2" />
              {importing ? "Importing…" : "Import bundle"}
            </Button>
          </div>
          {importMode === "overwrite" && (
            <div className="text-xs text-amber-600 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Overwrite <b>deletes every doc in the target collections first</b>. Only use this on an empty production DB.</span>
            </div>
          )}
          {lastImportResult && (
            <div className="rounded-lg border p-3 text-xs bg-muted/40">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={14} className="text-accent" />
                <b>Last import result</b>
                <Badge variant="outline">{lastImportResult.mode}</Badge>
                <span className="text-muted-foreground">· uploads written: {lastImportResult.uploads_written}, skipped: {lastImportResult.uploads_skipped}</span>
              </div>
              <div className="grid gap-1 sm:grid-cols-2">
                {Object.entries(lastImportResult.collections || {}).map(([name, r]) => (
                  <div key={name} className="flex justify-between border-b border-dashed py-1">
                    <span className="text-muted-foreground">{name}</span>
                    <span className="font-mono">+{r.restored} {r.skipped ? `(skip ${r.skipped})` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground pt-2 border-t">
            <b>Autopilot mode:</b> if you drop the bundle at <code>/app/migration/incoming/bundle.zip</code> before starting the production pod (via the code editor), the app will auto-restore on first boot when the database is empty. See <code>/app/MIGRATION.md</code> for full steps.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
